import React from 'react';

import Switch from '@liquidweb-dev/undercurrent/Switch';
import FormControl from '@liquidweb-dev/undercurrent/FormControl';
import FormHelperText from '@liquidweb-dev/undercurrent/FormHelperText';
import { useInput } from 'components/LWForm/LWForm';
import FormControlLabel from '@liquidweb-dev/undercurrent/FormControlLabel';

/**
 * @typedef PropsI
 * @property {string} name
 * @property {import('react').ReactNode} [helperText]
 * @property {import('@liquidweb-dev/undercurrent/FormControl').FormControlProps} [FormControlProps]
 * @property {import('@liquidweb-dev/undercurrent/FormControlLabel').FormControlLabelPropsI['label']} [label]
 * @property {import('@liquidweb-dev/undercurrent/FormControlLabel').FormControlLabelPropsI['description']} [description]
 * @property {import('@liquidweb-dev/undercurrent/FormControlLabel').FormControlLabelPropsI} [FormControlLabelProps]
 * @property {import('@liquidweb-dev/undercurrent/FormHelperText').FormHelperTextProps} [FormHelperTextProps]
 * */

/**
 * A checkbox input component to be used within the context of LWForm
 * @param {Omit<import('@liquidweb-dev/undercurrent/Switch').SwitchProps, 'name'> & PropsI} props
 */
const HookedSwitch = ({
	required,
	helperText,
	label,
	description,
	name,
	onBlur,
	onChange,
	FormControlProps,
	FormControlLabelProps,
	FormHelperTextProps,
	...rest
}) => {
	const { value, onValueChange, error, validate } = useInput(name);

	if (!name) {
		return null;
	}

	/** @param {React.ChangeEvent<HTMLInputElement>} event */
	const handleChange = (event) => {
		if (!event.target) {
			return;
		}

		onValueChange(event.target.checked);

		if (onChange) {
			onChange(event);
		}

		// validate on input change only if there is an existing error
		if (!error) {
			return;
		}

		validate(event.target);
	};

	/** @param {React.FocusEvent<HTMLInputElement, Element>} event */
	const handleBlur = (event) => {
		if (!event.target) {
			return;
		}

		if (onBlur) {
			onBlur(event);
		}

		validate(event.target);
	};

	return (
		<FormControl
			{...FormControlProps}
			required={required}
			error={Boolean(error)}
		>
			<FormControlLabel
				{...FormControlLabelProps}
				label={label}
				description={description}
				control={
					<Switch
						name={name}
						onBlur={handleBlur}
						onChange={handleChange}
						required={required}
						{...rest}
						checked={value}
					/>
				}
			/>
			{(helperText || error) && (
				<FormHelperText {...FormHelperTextProps}>
					{helperText || error}
				</FormHelperText>
			)}
		</FormControl>
	);
};

export default HookedSwitch;
